import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Zap, Shield, Terminal, Trash2, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const ObjectionCrusherModule = ({ token }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('normal');
  const [history, setHistory] = useState([]);
  const [streamingContent, setStreamingContent] = useState('');
  const messagesEndRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    try {
      const r = await axios.get(API + '/chat/history', { headers: { Authorization: 'Bearer ' + token } });
      setHistory(r.data || []);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchHistory(); }, [token, fetchHistory]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, streamingContent]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    setStreamingContent('');
    try {
      const response = await fetch(API + '/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({
          message: userMessage,
          persona: mode === 'warroom' ? 'selena-warroom' : 'selena-dncm',
          history: messages.map(m => ({ role: m.role, content: m.content }))
        })
      });
      if (!response.ok) throw new Error('Erreur stream ' + response.status);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const data = line.slice(5).trim();
          if (data === '[DONE]') break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.token) { fullContent += parsed.token; setStreamingContent(fullContent); }
          } catch (ex) {}
        }
      }
      setMessages(prev => [...prev, { role: 'assistant', content: fullContent || '...' }]);
      setStreamingContent('');
      fetchHistory();
    } catch (error) {
      toast.error('Erreur de liaison CORE_SELENA');
      console.error(error);
    } finally { setLoading(false); }
  };

  const clearChat = () => { setMessages([]); toast.success('Interface reinitialisee'); };

  return (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex items-center justify-between p-4 grid-card">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#00FF9D]/10 rounded-lg"><Shield className="w-5 h-5 text-[#00FF9D]" /></div>
          <div>
            <h2 className="font-heading text-sm tracking-tighter text-white uppercase">Selena Matrix</h2>
            <p className="text-[10px] font-mono text-[#888888]">Orchestrateur Strategique DNCM</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMode(mode === 'normal' ? 'warroom' : 'normal')}
            className={'p-2 rounded-lg transition-all ' + (mode === 'warroom' ? 'bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/50' : 'bg-[#444444]/20 text-[#888888] border border-white/10')}>
            <Zap className={'w-4 h-4 ' + (mode === 'warroom' ? 'fill-[#FFD700]' : '')} />
          </button>
          <button onClick={clearChat} className="p-2 rounded-lg bg-[#444444]/20 text-[#888888] border border-white/10 hover:text-white">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto space-y-4 p-2 custom-scrollbar" style={{ minHeight: '300px', maxHeight: '500px' }}>
        {messages.map((msg, i) => (
          <div key={i} className={'flex ' + (msg.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div className={'max-w-[85%] p-3 rounded-lg font-mono text-xs ' + (msg.role === 'user' ? 'bg-[#00FF9D]/10 border border-[#00FF9D]/30 text-white' : 'bg-[#111111] border border-white/5 text-[#cccccc]')}>
              <div className="flex items-center gap-2 mb-1 opacity-50">
                {msg.role === 'user' ? <Terminal className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                <span className="text-[10px] uppercase">{msg.role === 'user' ? 'Client' : 'Selena'}</span>
              </div>
              <p className="leading-relaxed whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && streamingContent && (
          <div className="flex justify-start">
            <div className="max-w-[85%] p-3 rounded-lg bg-[#111111] border border-[#00FF9D]/20 font-mono text-xs text-[#cccccc]">
              <div className="flex items-center gap-2 mb-1 opacity-50">
                <Shield className="w-3 h-3" /><span className="text-[10px] uppercase">Selena</span>
                <span className="text-[#00FF9D] animate-pulse">|</span>
              </div>
              <p className="leading-relaxed whitespace-pre-wrap">{streamingContent}</p>
            </div>
          </div>
        )}
        {loading && !streamingContent && (
          <div className="flex justify-start">
            <div className="bg-[#111111] border border-white/5 p-3 rounded-lg">
              <div className="flex gap-1">
                <div className="w-1 h-1 bg-[#00FF9D] animate-bounce" />
                <div className="w-1 h-1 bg-[#00FF9D] animate-bounce" style={{ animationDelay: '0.2s' }} />
                <div className="w-1 h-1 bg-[#00FF9D] animate-bounce" style={{ animationDelay: '0.4s' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={sendMessage} className="relative">
        <input type="text" value={input} onChange={e => setInput(e.target.value)}
          placeholder="Entrer une directive strategique..."
          className="w-full bg-[#111111] border border-white/10 rounded-xl p-4 pr-12 font-mono text-xs text-white focus:outline-none focus:border-[#00FF9D]/50"
          disabled={loading} />
        <button type="submit" disabled={loading || !input.trim()}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-[#00FF9D] hover:bg-[#00FF9D]/10 rounded-lg disabled:opacity-30">
          <Send className="w-4 h-4" />
        </button>
      </form>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid-card p-4">
          <span className="font-heading text-[10px] uppercase tracking-widest text-[#444444] block mb-3">LOGS RECENTS</span>
          <div className="space-y-2 max-h-[100px] overflow-y-auto text-[10px]">
            {history.length === 0 ? <p className="font-mono text-[#444444] text-center">Aucun log</p> : history.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between border-b border-white/5 pb-1">
                <span className="text-[#888888] truncate mr-2">{item.message}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <Clock className="w-2 h-2 text-[#444444]" />
                  <span className="text-[#444444]">{item.timestamp ? new Date(item.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '--:--'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid-card p-4 border-l-2 border-[#00FF9D]">
          <div className="flex items-center justify-between mb-3">
            <span className="font-heading text-[10px] uppercase text-[#00FF9D]">CORE_SELENA : LIVE</span>
            <span className="w-2 h-2 bg-[#00FF9D] shadow-[0_0_8px_#00FF9D]" />
          </div>
          <p className="font-mono text-[10px] text-[#888888] mb-1">ENDPOINT: /api/chat/stream</p>
          <p className="font-mono text-[10px] text-[#888888]">SSE STREAMING : ACTIF</p>
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/5">
            <CheckCircle className="w-3 h-3 text-[#00FF9D]" />
            <p className="font-mono text-[9px] text-[#00FF9D] uppercase">SOUVERAINETE OPERATIONNELLE</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObjectionCrusherModule;